import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/config";
import { listTimezones, DEFAULT_TIMEZONE } from "@/lib/timezones";
import { DATE_ORDER_OPTIONS } from "@/lib/csvDates";

// Club-wide settings (name, contact email, brand colour, time zone, date
// format, expiry reminders). Billing/plan management lives on its own page,
// shown as a sub-item under Account in the sidebar.

function apiError(error, fallback) {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg).filter(Boolean).join(" ") || fallback;
  return detail || fallback;
}

const selectClass = "h-9 rounded-md border border-zinc-200 bg-white text-sm px-2 max-w-full";

const AccountPage = () => {
  const { refreshOrg: refreshGlobalOrg } = useAuth();
  const [orgDetails, setOrgDetails] = useState(null);
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [brandColor, setBrandColor] = useState("#303030");
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [dateFormat, setDateFormat] = useState("DMY");
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [saving, setSaving] = useState(null); // which card is saving
  const timezones = listTimezones();

  const load = (data) => {
    setOrgDetails(data);
    setName(data.name || "");
    setContactEmail(data.contact_email || "");
    setBrandColor(data.brand_color || "#303030");
    setTimezone(data.timezone || DEFAULT_TIMEZONE);
    setDateFormat(data.date_format || "DMY");
    setRemindersEnabled(data.expiry_reminders_enabled !== false);
  };

  const fetchOrg = async () => {
    try {
      const { data } = await axios.get(`${API}/organizations/me`);
      load(data);
    } catch (error) {
      console.error("Error fetching organization:", error);
    }
  };

  useEffect(() => {
    fetchOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (card, patch, successMsg) => {
    setSaving(card);
    try {
      await axios.put(`${API}/organizations/me`, patch);
      toast.success(successMsg);
      await fetchOrg();
      await refreshGlobalOrg(); // theming, Members date format, etc. pick it up immediately
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error(apiError(error, "Failed to update settings"));
      await fetchOrg(); // roll the UI back to what's saved
    } finally {
      setSaving(null);
    }
  };

  if (!orgDetails) {
    return (
      <div className="p-4 sm:p-6 lg:p-12">
        <div className="text-zinc-500 text-sm">Loading...</div>
      </div>
    );
  }

  const detailsChanged =
    name.trim() !== (orgDetails.name || "") || contactEmail.trim() !== (orgDetails.contact_email || "");
  const regionChanged =
    timezone !== (orgDetails.timezone || DEFAULT_TIMEZONE) || dateFormat !== (orgDetails.date_format || "DMY");

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-4xl">
      <h1 className="text-2xl font-semibold text-zinc-900 mb-1">Account</h1>
      <p className="text-sm text-zinc-500 mb-8">Your club's settings</p>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8">
        <p className="text-xs uppercase tracking-wider text-zinc-500 mb-4">Club Details</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-sm text-zinc-600">Club / organisation name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="account-name-input" />
          </div>
          <div>
            <Label className="text-sm text-zinc-600">Contact email</Label>
            <Input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="mt-1.5"
              placeholder="club@example.com"
              data-testid="account-contact-email-input"
            />
            <p className="text-xs text-zinc-400 mt-1">Reply-to on pass emails, and where expiry reminders are sent.</p>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button
            variant="outline"
            onClick={() => save("details", { name: name.trim(), contact_email: contactEmail.trim() || null }, "Club details updated")}
            disabled={saving === "details" || !detailsChanged || !name.trim()}
            data-testid="account-save-details-btn"
          >
            {saving === "details" ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8">
        <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Brand Colour</p>
        <p className="text-sm text-zinc-600 mb-4">Used for your dashboard's accent colour and the emails your members receive.</p>
        <div className="flex items-center gap-3">
          <Input
            value={brandColor}
            onChange={(e) => setBrandColor(e.target.value)}
            className="w-32"
            placeholder="#303030"
            pattern="^#[0-9A-Fa-f]{6}$"
            title="A hex colour like #303030"
            data-testid="account-brand-color-input"
          />
          <input
            type="color"
            value={brandColor}
            onChange={(e) => setBrandColor(e.target.value)}
            className="w-12 h-9 rounded-md border border-zinc-200 cursor-pointer"
            data-testid="account-brand-color-picker"
          />
          <Button
            variant="outline"
            onClick={() => save("color", { brand_color: brandColor }, "Brand colour updated")}
            disabled={saving === "color" || brandColor === (orgDetails.brand_color || "#303030")}
            data-testid="account-save-brand-color-btn"
          >
            {saving === "color" ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8">
        <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Time Zone &amp; Date Format</p>
        <p className="text-sm text-zinc-600 mb-4">
          Memberships expire at midnight club time the day after their expiry date. The date format is used on the
          Members list and as the default for CSV imports.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Label className="text-sm text-zinc-600">Time zone</Label>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className={selectClass} data-testid="account-timezone-select">
            {!timezones.includes(timezone) && <option value={timezone}>{timezone.replace(/_/g, " ")}</option>}
            {timezones.map((tz) => (
              <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
            ))}
          </select>
          <Label className="text-sm text-zinc-600 sm:ml-2">Date format</Label>
          <select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} className={selectClass} data-testid="account-date-format-select">
            {DATE_ORDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <Button
            variant="outline"
            onClick={() => save("region", { timezone, date_format: dateFormat }, "Time zone & date format updated")}
            disabled={saving === "region" || !regionChanged}
            data-testid="account-save-region-btn"
          >
            {saving === "region" ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Membership Expiry Reminders</p>
            <p className="text-sm text-zinc-600">
              On the 1st of each month at 8am (club time) we email{" "}
              <span className="font-medium text-zinc-800">{orgDetails.contact_email || "your contact email"}</span>{" "}
              a list of members whose membership expires by the end of next month. No email is sent in months where nobody is expiring.
            </p>
          </div>
          <Switch
            checked={remindersEnabled}
            disabled={saving === "reminders"}
            onCheckedChange={(checked) => {
              setRemindersEnabled(checked);
              save("reminders", { expiry_reminders_enabled: checked }, checked ? "Expiry reminders on" : "Expiry reminders off");
            }}
            data-testid="account-expiry-reminders-switch"
          />
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
